import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ViewEncapsulation, computed, inject } from '@angular/core';
import { SettingsPanelComponent, SettingsPanelMode } from '../settings.panel/settings.panel.component';
import { CommandPaletteService } from './command.palette.service';
import { CommandPaletteContext } from './command.palette.types';
import { BadgeComponent } from '@workbuddy/ui/components/badge';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { commandPaletteHostBridgeStyles, commandPaletteHostStyleId } from './command.palette.host.theme';

@Component({
  selector: 'command-palette-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [SettingsPanelComponent, BadgeComponent],
  templateUrl: './command.palette.host.component.html',
  styleUrls: ['./command.palette.host.component.less'],
})
export class CommandPaletteHostComponent {

  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly palette = inject(CommandPaletteService);

  protected readonly SettingsPanelMode = SettingsPanelMode;

  protected readonly $pending = computed(() => this.palette.$openContext());
  protected readonly $context = computed<CommandPaletteContext | null>(() => this.$pending()?.context ?? null);
  protected readonly $isOpen = computed(() => this.$context() != null);

  public constructor() {

    DesignSystem.Mount(this.document, commandPaletteHostStyleId, commandPaletteHostBridgeStyles);
    const keydownHandler = (event: KeyboardEvent) => this.onWindowKeydown(event);
    this.document.defaultView?.addEventListener('keydown', keydownHandler, true);
    this.destroyRef.onDestroy(() => this.document.defaultView?.removeEventListener('keydown', keydownHandler, true));
  }

  protected onBackdropClick(): void {

    this.palette.close();
  }

  protected onSettingsPanelClosed(): void {

    this.palette.close();
  }

  private onWindowKeydown(event: KeyboardEvent): void {

    if (this.isCommandK(event)) {
      if (!this.palette.hasCommandKContext()) return;

      event.preventDefault();
      event.stopPropagation();
      void this.palette.openCommandK();
      return;
    }

    if (!this.$isOpen() || event.key !== 'Escape') return;
    if (this.$context()?.settingsPanel) return;

    event.preventDefault();
    event.stopPropagation();
    this.palette.close();
  }

  private isCommandK(event: KeyboardEvent): boolean {

    if (event.key?.toLowerCase() !== 'k') return false;
    return /Mac|iPod|iPhone|iPad/i.test(this.document.defaultView?.navigator.platform ?? '') ? event.metaKey : event.ctrlKey;
  }
}
